import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Text,
  TokenTextAppearance,
  styled,
  TextTag,
  DisplayTag,
} from '@volkswagen-onehub/components-core';
import { GiveawayContext } from '@/state/state';
import { minimalScreenWidth } from '@/common/breakpoints';

export interface YourCarProps {
  requiresSmallerBottomSpacing: boolean;
}

const StyledTextContainer = styled.div<YourCarProps>`
  padding-left: ${(props) => props.theme.size.dynamic0150};
  padding-right: ${(props) => props.theme.size.dynamic0150};
  padding-top: ${(props) => props.theme.size.dynamic0150};
  padding-bottom: ${(props) =>
    props.requiresSmallerBottomSpacing
      ? props.theme.size.dynamic0050
      : props.theme.size.dynamic0150};

  @media screen and ${minimalScreenWidth.l} {
    padding-left: ${(props) => props.theme.size.dynamic0250};
    padding-bottom: ${(props) =>
      props.requiresSmallerBottomSpacing
        ? props.theme.size.dynamic0050
        : props.theme.size.dynamic0150};
    padding-right: ${(props) => props.theme.size.dynamic0250};
    padding-top: ${(props) => props.theme.size.dynamic0150};
  }

  @media screen and ${minimalScreenWidth.xl} {
    padding-left: ${(props) => props.theme.size.dynamic0450};
    padding-bottom: ${(props) =>
      props.requiresSmallerBottomSpacing
        ? props.theme.size.dynamic0050
        : props.theme.size.dynamic0150};
    padding-right: ${(props) => props.theme.size.dynamic0450};
    padding-top: ${(props) => props.theme.size.dynamic0150};
  }
`;

const StyledDisplayTag = styled(DisplayTag)`
  margin-top: ${(props) => props.theme.size.static250};
  padding: ${(props) => props.theme.size.static250};
`;

const StyledModelText = styled(Text)`
  max-width: 580px;
  width: 100%;
  overflow-wrap: normal;
  word-break: normal;
  hyphens: none;

  @media screen and ${minimalScreenWidth.l} {
    max-width: none;
  }
`;

export const YourCar = (props: YourCarProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { carModel, marketingCode } = useContext(GiveawayContext);
  const { requiresSmallerBottomSpacing } = props;
  const isVIN = marketingCode === 'VIN';

  if (!carModel) {
    return null;
  }

  return (
    <section data-testid="yourcar-container">
      <StyledTextContainer
        requiresSmallerBottomSpacing={requiresSmallerBottomSpacing}
        data-testid="yourcar-text-container"
      >
        <Text tag={TextTag.h2} bold appearance={TokenTextAppearance.headline400}>
          {t('header.model')}
        </Text>

        <StyledModelText appearance={TokenTextAppearance.headline400}>{carModel}</StyledModelText>

        {!isVIN && (
          <StyledDisplayTag
            appearance="info"
            text={`${t('sideBar.marketingCode')}: ${marketingCode}`}
          />
        )}
      </StyledTextContainer>
    </section>
  );
};
